
import React, { useEffect, useState, useMemo } from 'react';
import { createShippingAddress, updateShippingAddress, getShippingAddresses, closeShippingAddressForm, deleteShippingAddress } from '../../Reducers/actions/shippingAddressActions';
import { getCountriesAndRegions } from '../../Reducers/actions/shippingMethodsActions';
import Icon from '../Icon/Icon';
import ModalMessage from '../ModalMessage/ModalMessage';
import CustomSelect from '../CustomSelect/CustomSelect';
import './AddShipping.scss';
import { useDispatch, useSelector } from 'react-redux';

const AddShipping = ({ editingAddress, setEditingAddress, handleDelete, onSuccess }) => {
    const dispatch = useDispatch();
    const shippingMethodsState = useSelector((state) => state.shippingMethods);
    const countriesRegions = (shippingMethodsState && shippingMethodsState.countriesRegions) || null;
    const countriesRegionsLoading = (shippingMethodsState && shippingMethodsState.countriesRegionsLoading) || false;

    const [formErrors, setFormErrors] = useState({});
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        country: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        postal_code: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Extract countries from the backend data
    const countries = useMemo(() => {
        if (!countriesRegions) return [];
        
        const allCountries = countriesRegions.allCountries || [];
        // Extract unique countries and sort them alphabetically
        const uniqueCountries = Array.from(
            new Map(
                allCountries.map(country => {
                    const countryName = country.name || country.country_name || '';
                    const countryCode = country.code || country.country_code || '';
                    return [countryName, { name: countryName, code: countryCode }];
                }).filter(([name]) => name) // Filter out entries with no name
            ).values()
        ).sort((a, b) => a.name.localeCompare(b.name));
        
        return uniqueCountries;
    }, [countriesRegions]);

    // Fetch countries on mount
    useEffect(() => {
        if (!countriesRegions && !countriesRegionsLoading) {
            dispatch(getCountriesAndRegions());
        }
    }, [dispatch, countriesRegions, countriesRegionsLoading]);

    // Set default country when countries are loaded
    useEffect(() => {
        if (countries.length > 0 && !formData.country && !editingAddress) {
            setFormData(prev => ({
                ...prev,
                country: countries[0].name
            }));
        }
    }, [countries, editingAddress]);

    useEffect(() => {
        if (editingAddress) {
            setFormData({
                first_name: editingAddress.first_name || '',
                last_name: editingAddress.last_name || '',
                country: editingAddress.country || (countries.length > 0 ? countries[0].name : ''),
                address_line_1: editingAddress.address_line_1 || '',
                address_line_2: editingAddress.address_line_2 || '',
                city: editingAddress.city || '',
                postal_code: editingAddress.postal_code || ''
            });
        }
    }, [editingAddress, countries]);
    const validateForm = () => {
        const errors = {};
        if (!formData.first_name.trim()) errors.first_name = 'First name is required';
        if (!formData.last_name.trim()) errors.last_name = 'Last name is required';
        if (!formData.country.trim()) errors.country = 'Country is required';
        if (!formData.address_line_1.trim()) errors.address_line_1 = 'Address line 1 is required';
        if (!formData.city.trim()) errors.city = 'City is required';
        if (!formData.postal_code.trim()) errors.postal_code = 'Postal code is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const payload = {
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                country: formData.country.trim(),
                address_line_1: formData.address_line_1.trim(),
                address_line_2: formData.address_line_2.trim() || null,
                city: formData.city.trim(),
                postal_code: formData.postal_code.trim()
            };

            if (editingAddress) {
                const result = await dispatch(updateShippingAddress(editingAddress.address_id, payload));
                if (result?.success) {
                    setEditingAddress(null);
                    resetForm();
                    dispatch(getShippingAddresses());
                    if (onSuccess) onSuccess();
                }
            } else {
                const result = await dispatch(createShippingAddress(payload));
                if (result?.success) {
                    resetForm();
                    dispatch(getShippingAddresses());
                    if (onSuccess) onSuccess();
                }
            }
        } catch (error) {
            console.error('Error saving address:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            first_name: '',
            last_name: '',
            country: countries.length > 0 ? countries[0].name : '',
            address_line_1: '',
            address_line_2: '',
            city: '',
            postal_code: ''
        });
        setFormErrors({});
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error for this field
        if (formErrors[field]) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const confirmDelete = async () => {
        if (!editingAddress) return;
        
        setIsDeleting(true);
        try {
            const result = await dispatch(deleteShippingAddress(editingAddress.address_id));
            if (result?.success) {
                setShowDeleteModal(false);
                setEditingAddress(null);
                resetForm();
                dispatch(getShippingAddresses());
                dispatch(closeShippingAddressForm());
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error('Error deleting address:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="add-shipping-addresses-container">
            <p className='fs-sm2 fw-bold fg-dark ta-center'>{editingAddress ? 'Edit shipping address' : 'New shipping address'}</p>
            <form className="shipping-address-form" onSubmit={handleSubmit}>
                <input
                    className={`input-main ${formErrors.first_name ? 'input-error' : ''} mt-16`}
                    type="text"
                    placeholder="First name"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                />
                {formErrors.first_name && <p className="form-error">{formErrors.first_name}</p>}

                <input
                    className={`input-main ${formErrors.last_name ? 'input-error' : ''}`}
                    type="text"
                    placeholder="Last name"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                />
                {formErrors.last_name && <p className="form-error">{formErrors.last_name}</p>}

                <div className="country-select-wrapper">
                    <CustomSelect
                        options={countries.map((country) => ({
                            value: country.name,
                            label: country.name,
                        }))}
                        value={formData.country}
                        onChange={(value) => handleInputChange('country', value)}
                        placeholder="Select country"
                        searchable
                        searchPlaceholder="Search for a country..."
                        disabled={countriesRegionsLoading || countries.length === 0}
                        loading={countriesRegionsLoading}
                        loadingText="Loading countries..."
                        emptyText="No countries available"
                        error={!!formErrors.country}
                    />
                </div>
                {formErrors.country && <p className="form-error">{formErrors.country}</p>}

                <input
                    className={`input-main ${formErrors.address_line_1 ? 'input-error' : ''}`}
                    type="text"
                    placeholder="Address line 1"
                    value={formData.address_line_1}
                    onChange={(e) => handleInputChange('address_line_1', e.target.value)}
                />
                {formErrors.address_line_1 && <p className="form-error">{formErrors.address_line_1}</p>}

                <input
                    className="input-main"
                    type="text"
                    placeholder="Address line 2 (optional)"
                    value={formData.address_line_2}
                    onChange={(e) => handleInputChange('address_line_2', e.target.value)}
                />
                <div className="d-flex f-row gap-16">
                    <div>
                    <input
                        className={`input-main input-main-city ${formErrors.city ? 'input-error' : ''}`}
                        type="text"
                        placeholder="City"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                    />
                    {formErrors.city && <p className="form-error">{formErrors.city}</p>}
                    </div>
                    <div>
                    <input
                        className={`input-main input-main-postal-code ${formErrors.postal_code ? 'input-error' : ''}`}
                        type="text"
                        placeholder="Postal code"
                        value={formData.postal_code}
                        onChange={(e) => handleInputChange('postal_code', e.target.value)}
                    />
                    {formErrors.postal_code && <p className="form-error">{formErrors.postal_code}</p>}
                    </div>
                </div>
                {editingAddress && (
                    <div className="delete-address-link c-pointer" onClick={() => setShowDeleteModal(true)}>
                        <Icon alias="trash_v2" width={16} color="transparent" stroke="#FF3232" strokeWidth={2} />
                        <span className="fg-red-pink fs-xxs fw-bold">DELETE ADDRESS</span>
                    </div>
                )}

                <button
                    type="submit"
                    className="btn red-btn btn-full"
                    disabled={isSubmitting}
                    style={{ height: '64px', marginTop: '24px' }}
                >
                    SAVE ADDRESS
                </button>

                <p
                    className="fs-xxs fg-dark fw-bold mb-16 mt-16 ta-center c-pointer td-underline"
                    onClick={() => {
                        setEditingAddress(null);
                        resetForm();
                        dispatch(closeShippingAddressForm());
                    }}
                >
                    CANCEL
                </p>
            </form>

            {showDeleteModal && (
                <ModalMessage onClose={() => setShowDeleteModal(false)}>
                    <div className="delete-confirmation d-flex f-column aic jcc">
                        <p className="fs-sm2 fw-bold mb-32 mt-32">
                            Are you sure you want to delete this address?
                        </p>
                        <button
                            className="btn red-outline-btn btn-full aic jcc fw-extra-bold mb-24"
                            style={{ height: '64px' }}
                            onClick={confirmDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? 'DELETING...' : 'YES, DELETE'}
                        </button>
                        <p
                            className="fs-xxs fg-dark fw-bold mb-16 mt-0 c-pointer td-underline"
                            onClick={() => setShowDeleteModal(false)}
                        >
                            CANCEL
                        </p>
                    </div>
                </ModalMessage>
            )}
        </div>
    );
}




export default AddShipping;